import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { DialogProps } from '@toolpad/core/useDialogs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ClassData } from '../../../types/class';

export function ClassCreateDialog({ payload, open, onClose }: DialogProps<{ data?: Pick<ClassData, 'className' | 'schoolYear'> } | null, Pick<ClassData, 'className' | 'schoolYear'> | null>) {
    const { t } = useTranslation();

    // Generate academic years starting from 2020 (including next year)
    const generateAcademicYears = () => {
        const startYear = 2020;
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear + 1; year >= startYear; year--) {
            years.push(`${year}-${year + 1}`);
        }
        return years;
    };

    const academicYears = generateAcademicYears();
    const defaultAcademicYear = payload?.data?.schoolYear || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;

    // ui
    const [saving, setSaving] = React.useState(false);
    const [classNameError, setClassNameError] = React.useState(false);
    const [classNameErrorMessage, setClassNameErrorMessage] = React.useState('');
    const [schoolYearError, setSchoolYearError] = React.useState(false);
    const [schoolYearErrorMessage, setSchoolYearErrorMessage] = React.useState('');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (classNameError || schoolYearError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const className = data.get('class-name') as string;
        const schoolYear = data.get('school-year') as string;

        setSaving(true);
        await onClose({
            className,
            schoolYear,
        });
        setSaving(false);
    };

    const validateInputs = () => {
        const className = document.getElementById('class-name') as HTMLInputElement;
        const schoolYear = document.querySelector('[name="school-year"]') as HTMLSelectElement;

        let isValid = true;

        if (!className.value || className.value.trim().length === 0) {
            setClassNameError(true);
            setClassNameErrorMessage(t('class.form.invalidClassName'));
            isValid = false;
        } else {
            setClassNameError(false);
            setClassNameErrorMessage('');
        }

        if (!schoolYear || !schoolYear.value) {
            setSchoolYearError(true);
            setSchoolYearErrorMessage(t('class.form.invalidSchoolYear'));
            isValid = false;
        } else {
            setSchoolYearError(false);
            setSchoolYearErrorMessage('');
        }

        return isValid;
    };

    return (
        <Dialog fullWidth open={open} onClose={() => onClose(null)}>
            <Box component='form' onSubmit={handleSubmit} noValidate>
                <DialogTitle>{payload ? t('class.actions.editClass') : t('class.actions.createClass')}</DialogTitle>
                <DialogContent>
                    <Stack sx={{ pt: 1 }} gap={2}>
                        <TextField
                            id='class-name'
                            name='class-name'
                            label={t('class.label.className')}
                            fullWidth
                            autoFocus
                            required
                            error={classNameError}
                            helperText={classNameErrorMessage}
                            defaultValue={payload?.data?.className || ''}
                        />
                        <FormControl fullWidth required error={schoolYearError}>
                            <InputLabel id='school-year-label'>{t('class.label.schoolYear')}</InputLabel>
                            <Select labelId='school-year-label' id='school-year' name='school-year' label={t('class.label.schoolYear')} defaultValue={defaultAcademicYear}>
                                {academicYears.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                            {schoolYearError && <FormHelperText>{schoolYearErrorMessage}</FormHelperText>}
                        </FormControl>
                    </Stack>
                </DialogContent>
                <Stack direction='row' sx={{ justifyContent: 'flex-end', gap: 1, p: 1 }}>
                    <Button
                        onClick={async () => {
                            onClose(null);
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} variant='contained' type='submit' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                        {t('common.save')}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}
